import * as React from 'react';
import { Link } from "react-router-dom";
import { useQuery } from '../../Components/Query';
import { Column, Table } from "../../Components/QueryTable";
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { Authorization } from "../../misc/components/Authorization";
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { MatchSortHeader } from '../../Components/MatchSortHeader';
import { useState } from 'react';
import { useEffect } from 'react';
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { ConfigurableDataItemIds } from '../../misc/api/ApiConfigurableDataItems';
import { isColumnHidden } from '../../Subjects/isColumnHidden';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';
import { ImportSites } from './ImportSites';
import { uniqBy } from '../../misc/ArrayFunctions';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from '@fortawesome/free-solid-svg-icons';

export const SitesTable = () => {

    const { study, countries, dataItemConfigurationCollection } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const { data, applyFilters, refresh } = useQuery();
    const client = useWebPortalApiClient();
    const [siteOptions, setSiteOptions] = useState([]);
    const [nameOptions, setNameOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    const [contactOptions, setContactOptions] = useState([]);
    const [importSitesVisible, setImportSitesVisible] = useState(false);

    useEffect(() => {

        const getSites = async () => {

            const sites = await client.client.getSites('id,number', `study/id eq ${study.id}`, 'number', undefined, undefined, undefined);
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };

        getSites();
    }, []);

    useEffect(() => {

        const getFilterOptions = async () => {

            const sites = await client.client.getSites('name,contact', `studyId eq ${study.id}`, 'name', undefined, undefined, undefined);
            const nameOptions = uniqBy(sites.value.map(s => ({
                value: s.name,
                label: s.name,
            })), (a, b) => a.value === b.value);

            setNameOptions(nameOptions);

            const contactOptions = uniqBy(sites.value.filter(s => s.contact).map(s => ({
                value: s.contact,
                label: s.contact,
            })), (a, b) => a.value === b.value);

            setContactOptions(contactOptions);

            const countryOptions = countries.map(s => ({
                value: s.countryCode,
                label: s.name,
            }));

            setCountryOptions(countryOptions);
        };

        getFilterOptions();
    }, []);

    const handleClearFilters = () => applyFilters([]);

    const getColumnName = (itemId: ConfigurableDataItemIds) => {
        if (dataItemConfigurationCollection === null)
            return '';

        return dataItemConfigurationCollection.find(x => x.configurableDataItemId === itemId).displayText;
    };

    const menuFormatter = (id) => {
        return (
            <Authorization
                permissionIdsAny={[PermissionEnum.ViewSites, PermissionEnum.EditSite]}
                studyId={study.id}
                currentPermissions={currentUserPermissions}>
                <div className="text-center">
                    <div className="btn-group" role="group">
                        <button type="button" className="btn btn-primary btn-xs dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" data-automationid={`options_${id}`}>
                            <FontAwesomeIcon aria-hidden="true" icon={faBars} />
                        </button>
                        <ul className="dropdown-menu pull-right scrollable-menu" role="menu">
                            <Authorization
                                permissionId={PermissionEnum.ViewSites}
                                studyId={study.id}
                                currentPermissions={currentUserPermissions}>
                                <li>
                                    <Link data-automationid={`view-site_${id}`} to={`/sites/view/${id}`}>View</Link>
                                </li>
                            </Authorization>
                            <Authorization
                                permissionId={PermissionEnum.EditSite}
                                studyId={study.id}
                                currentPermissions={currentUserPermissions}
                            >
                                <li>
                                    <Link data-automationid={`edit-site_${id}`} to={`/sites/Edit/${id}`}>Edit</Link>
                                </li>
                            </Authorization>
                        </ul>
                    </div>
                </div>
            </Authorization>
        );
    };

    return (
        <ErrorBoundary>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary" data-automationid="SitesTableHeader">Sites</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionIdsAny={[PermissionEnum.CreateSite]}>
                            <Link id="createNew" className="btn btn-primary" to="/Sites/Create">
                                Create New
                            </Link>&nbsp;
                        </Authorization>
                        <button className="btn btn-primary" onClick={handleClearFilters}>Clear All Filters</button>&nbsp;
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionIdsAny={[PermissionEnum.ImportSites]}>
                            <button className="btn btn-primary btn-outline" onClick={() => setImportSitesVisible(true)}>Import Sites</button>
                        </Authorization>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <ErrorBoundary>
                                <div id="SitesTable" className="table-responsive" data-automationid="SitesTable">
                                    <Table data={data}>
                                        <Column field="number" automationId="number" header={<MatchSortHeader automationId="number" title={getColumnName(ConfigurableDataItemIds.SiteNumber)} field="id" options={siteOptions} sortField="number" />} formatter={n => n} />
                                        {isColumnHidden(ConfigurableDataItemIds.SiteName, dataItemConfigurationCollection) ?
                                            null
                                            : <Column field="name" automationId="name" header={<MatchSortHeader title={getColumnName(ConfigurableDataItemIds.SiteName)} field="name" options={nameOptions} sortField="name" automationId="name" />} formatter={n => n} />
                                        }
                                        {isColumnHidden(ConfigurableDataItemIds.SiteCountry, dataItemConfigurationCollection) ?
                                            null
                                            : <Column field="country" automationId="country" header={<MatchSortHeader title={getColumnName(ConfigurableDataItemIds.SiteCountry)} field="country" options={countryOptions} sortField="country" automationId="country" />} formatter={c => c !== null ? countries.find(cs => cs.countryCode === c)?.name : null} />
                                        }
                                        {isColumnHidden(ConfigurableDataItemIds.SiteInvestigator, dataItemConfigurationCollection) ?
                                            null
                                            : <Column field="contact" automationId="contact" header={<MatchSortHeader title={getColumnName(ConfigurableDataItemIds.SiteInvestigator)} field="contact" options={contactOptions} sortField="contact" automationId="contact" />} formatter={c => c !== null ? c : '-'} />
                                        }
                                        <Column field="id" formatter={menuFormatter} />
                                    </Table>
                                </div>
                            </ErrorBoundary>
                        </div>
                    </div>
                </div>
            </div>
            {importSitesVisible ? <ImportSites onClose={() => { setImportSitesVisible(false); refresh() }} /> : null}
        </ErrorBoundary>
    );
};
